import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Maximize2,
  Bed,
  Calendar,
  Lock,
  Phone,
  Mail,
  Loader2,
  Sparkles,
  Building2,
  Tag,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_8f20c47c-6306-4c92-aa61-f6db9e4c740d/artifacts/cc7z0gyj_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2010_37_30%20AM.png";
const PLACEHOLDER =
  "https://images.unsplash.com/photo-1568605114967-8130f3a36994?auto=format&fit=crop&w=1600&q=70";

const formatINR = (n) => {
  const num = Math.round(Number(n) || 0);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(num % 10000000 === 0 ? 0 : 2)} Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(num % 100000 === 0 ? 0 : 2)} L`;
  return `₹${num.toLocaleString("en-IN")}`;
};

// Lazy-load the Razorpay Checkout.js script once and cache it on window.
const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

export default function ListingDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const coupon = (searchParams.get("coupon") || "").trim().toUpperCase();
  const navigate = useNavigate();
  const { isAuthed, requireAuth, user } = useAuth();
  const [listing, setListing] = useState(null);
  const [unlocked, setUnlocked] = useState(null); // {phone, email, name} after unlock
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    document.title = "Listing · Zumroo";
    (async () => {
      try {
        const [r, c] = await Promise.all([api.get(`/listings/${id}`), api.get("/content")]);
        setListing(r.data);
        setContent(c.data);
        document.title = `${r.data.property_type || "Listing"} in ${r.data.locality || "Pune"} · Zumroo`;
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <div className="flex items-center gap-2 text-[#5C6B62]">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading listing…
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-[#F7F6F2]">
        <Navbar logoUrl={LOGO_URL} content={content || { play_store_url: "#" }} transparent={false} />
        <div className="container-z pt-32 pb-20 text-center">
          <div className="text-[#5C6B62]">Listing not found.</div>
          <Link to="/listings" className="btn-primary mt-4 inline-flex">
            <ArrowLeft className="w-4 h-4" /> Back to listings
          </Link>
        </div>
      </div>
    );
  }

  const images = listing.images && listing.images.length > 0 ? listing.images : [PLACEHOLDER];
  const fee = listing.listing_type === "Sale" ? content?.price_premium || "₹79" : content?.price_basic || "₹49";
  const isNew = listing.created_at && Date.now() - new Date(listing.created_at).getTime() < 7 * 86400 * 1000;

  return (
    <div className="bg-[#F7F6F2] min-h-screen">
      <Navbar logoUrl={LOGO_URL} content={content || { play_store_url: "#" }} transparent={false} />

      <main className="container-z pt-20 sm:pt-24 pb-16">
        <button
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/listings"))}
          className="flex items-center gap-1 text-sm text-[#5C6B62] hover:text-[#1A231E] mb-5"
          data-testid="listing-back-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Back to listings
        </button>

        <div className="grid lg:grid-cols-12 gap-8">
          {/* Gallery + details */}
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="zcard overflow-hidden"
            >
              <div className="relative">
                <img
                  src={images[activeImg]}
                  alt=""
                  className="w-full h-[260px] sm:h-[420px] object-cover bg-[#E4E1D9]"
                  data-testid="listing-detail-image"
                />
                <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-[#C86646] text-white text-xs font-bold tracking-wider uppercase">
                  {listing.listing_type || "Sale"}
                </span>
                <span className="absolute top-4 right-4 px-3 py-1.5 rounded-full bg-[rgba(30,63,51,0.85)] text-white text-xs font-semibold flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {listing.city || "Pune"}
                </span>
                {isNew && (
                  <span className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-[#2D6A4F] text-white text-xs font-bold flex items-center gap-1">
                    <Sparkles className="w-3.5 h-3.5" /> Recently Added
                  </span>
                )}
              </div>
              {images.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto">
                  {images.map((img, i) => (
                    <button
                      key={img}
                      onClick={() => setActiveImg(i)}
                      className={`flex-shrink-0 w-20 h-16 rounded-lg overflow-hidden border-2 ${
                        i === activeImg ? "border-[#C86646]" : "border-transparent"
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="zcard p-6 sm:p-8 mt-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-display text-3xl sm:text-4xl font-bold text-[#1A231E] tracking-tight">
                    {formatINR(listing.listing_type === "Rent" ? (listing.rent_amount || listing.price) : (listing.price || listing.rent_amount))}
                    {listing.listing_type === "Rent" && (
                      <span className="text-base text-[#5C6B62] font-medium">/mo</span>
                    )}
                  </div>
                  <div className="mt-1 text-base text-[#1A231E]">
                    {listing.property_type}
                    {listing.bedrooms ? ` · ${listing.bedrooms} BHK` : ""} in {listing.locality}
                    {listing.society_name && (
                      <span className="text-[#1E3F33] font-semibold"> · {listing.society_name}</span>
                    )}
                  </div>
                </div>
                {listing.human_id && (
                  <span className="text-xs font-bold tracking-wider bg-[#EAF1ED] text-[#1E3F33] px-2.5 py-1 rounded-full">
                    {listing.human_id}
                  </span>
                )}
              </div>

              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat icon={Maximize2} label="Carpet Area" value={`${listing.carpet_area || listing.area_sqft || "—"} sqft`} />
                <Stat icon={Bed} label="Configuration" value={listing.configuration || (listing.bedrooms ? `${listing.bedrooms} BHK` : "—")} />
                <Stat icon={Building2} label="Furnishing" value={listing.furnishing || "—"} />
                <Stat icon={ShieldCheck} label="Possession" value={listing.possession_status || "—"} />
                <Stat icon={Tag} label="Transaction" value={listing.transaction_type || listing.listing_type || "—"} />
                <Stat icon={Building2} label="Floor" value={listing.floor_details || "—"} />
                {listing.listing_type === "Rent" && (
                  <>
                    <Stat icon={Tag} label="Rent / mo" value={listing.rent_amount ? formatINR(listing.rent_amount) : "—"} />
                    <Stat icon={Tag} label="Deposit" value={listing.deposit ? formatINR(listing.deposit) : "—"} />
                  </>
                )}
                <Stat
                  icon={Calendar}
                  label="Posted"
                  value={listing.created_at ? new Date(listing.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
                />
              </div>

              {listing.description && (
                <div className="mt-6 pt-6 border-t border-[#E4E1D9]">
                  <div className="label-mono mb-2">About this property</div>
                  <p className="text-[#1A231E] leading-relaxed whitespace-pre-line">{listing.description}</p>
                </div>
              )}

              {listing.amenities && listing.amenities.length > 0 && (
                <div className="mt-6 pt-6 border-t border-[#E4E1D9]">
                  <div className="label-mono mb-3">Amenities</div>
                  <div className="flex flex-wrap gap-2">
                    {listing.amenities.map((a) => (
                      <span key={a} className="text-xs font-semibold bg-[#EAF1ED] text-[#1E3F33] px-3 py-1.5 rounded-full flex items-center gap-1">
                        <Tag className="w-3 h-3" /> {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {listing.address && (
                <div className="mt-6 pt-6 border-t border-[#E4E1D9]">
                  <div className="label-mono mb-2">Location</div>
                  <div className="flex items-start gap-2 text-[#1A231E]">
                    <MapPin className="w-4 h-4 mt-0.5 text-[#1E3F33] flex-shrink-0" />
                    <span>{listing.address}</span>
                  </div>
                </div>
              )}
            </motion.div>
          </div>

          {/* Unlock card */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4 }}
              className="zcard p-6 sticky top-20"
            >
              <div className="label-mono mb-2">Owner contact</div>
              <div className="flex items-center gap-3 p-4 bg-[#FBEFE9] rounded-2xl mb-4">
                <Lock className="w-5 h-5 text-[#C86646]" />
                <div>
                  <div className="font-bold text-[#1A231E]">{unlocked ? "Unlocked" : "Preview"}</div>
                  <div className="text-xs text-[#5C6B62]">
                    {unlocked ? "Full owner details revealed below" : `Unlock for ${fee} to reveal full name & phone`}
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-5">
                <div className="flex items-center gap-3 text-sm">
                  <Building2 className="w-4 h-4 text-[#5C6B62]" />
                  <span className={`font-medium ${unlocked ? "text-[#1A231E]" : "text-[#5C6B62]"}`} data-testid="listing-owner-name">
                    {unlocked ? (unlocked.name || listing.contact_name) : (listing.contact_name || "—")}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <Phone className="w-4 h-4 text-[#5C6B62]" />
                  <span className={`font-mono ${unlocked ? "text-[#1A231E]" : "text-[#5C6B62]"}`} data-testid="listing-phone">
                    {unlocked ? unlocked.phone : (listing.contact_phone || "—")}
                  </span>
                </div>
                {(unlocked?.email || listing.contact_email) && (
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-[#5C6B62]" />
                    <span className={`font-mono ${unlocked ? "text-[#1A231E]" : "text-[#5C6B62]"}`} data-testid="listing-email">
                      {unlocked ? (unlocked.email || "—") : "•••••@•••••"}
                    </span>
                  </div>
                )}
              </div>

              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  if (!isAuthed) {
                    requireAuth({ redirectTo: `/listing/${id}` });
                    return;
                  }
                  if (unlocked) return;
                  setUnlockLoading(true);

                  // Helper: kicks off Razorpay Checkout for the paid unlock path.
                  // Called either when the backend returns 402 (free view used)
                  // OR when the user explicitly chooses to pay.
                  const startPaidUnlock = async () => {
                    try {
                      const ok = await loadRazorpayScript();
                      if (!ok || !window.Razorpay) {
                        toast.error("Could not load secure payments. Refresh and try again.");
                        return;
                      }
                      const { data: order } = await api.post("/unlocks/order", { listing_id: id, coupon: coupon || undefined });
                      if (order.already_unlocked) {
                        // Defensive: backend says this user has already paid for this listing.
                        const { data: u } = await api.post("/unlocks", { listing_id: id });
                        setUnlocked({
                          name: u.contact_name || listing.contact_name,
                          phone: u.contact_phone || listing.contact_phone,
                          email: u.contact_email || listing.contact_email,
                        });
                        toast.success("Already unlocked");
                        return;
                      }
                      if (!order.razorpay_enabled || !order.key_id) {
                        toast.error("Payments are not yet enabled on this environment.");
                        return;
                      }
                      const rzp = new window.Razorpay({
                        key: order.key_id,
                        amount: order.amount,
                        currency: order.currency || "INR",
                        name: "Zumroo",
                        description: `Unlock owner contact${order.human_id ? " · " + order.human_id : ""}`,
                        order_id: order.order_id,
                        prefill: {
                          name: order.name || user?.name || "",
                          email: order.email || user?.email || "",
                          contact: order.phone || user?.phone || "",
                        },
                        theme: { color: "#1E3F33" },
                        handler: async function (resp) {
                          try {
                            const { data: ok } = await api.post("/unlocks/verify", {
                              listing_id: id,
                              razorpay_order_id: resp.razorpay_order_id,
                              razorpay_payment_id: resp.razorpay_payment_id,
                              razorpay_signature: resp.razorpay_signature,
                            });
                            if (ok.ok || ok.verified) {
                              setUnlocked({
                                name: ok.contact_name || listing.contact_name,
                                phone: ok.contact_phone || listing.contact_phone,
                                email: ok.contact_email || listing.contact_email,
                              });
                              toast.success(`Payment of ₹${order.amount_inr} successful — owner contact unlocked`);
                            } else {
                              toast.error("Payment captured but verification failed. Contact support.");
                            }
                          } catch (err) {
                            toast.error(formatErr(err));
                          }
                        },
                        modal: {
                          ondismiss: () => {
                            toast.message("Payment cancelled");
                          },
                        },
                      });
                      rzp.on("payment.failed", (resp) => {
                        toast.error(`Payment failed: ${resp.error?.description || resp.error?.code || "unknown"}`);
                      });
                      rzp.open();
                    } catch (err) {
                      toast.error(formatErr(err));
                    }
                  };

                  // Try the free-view path first. If exhausted (402), upgrade to paid.
                  try {
                    const { data } = await api.post("/contact-views", { listing_id: id });
                    setUnlocked({
                      name: data.contact_name || listing.contact_name,
                      phone: data.contact_phone || listing.contact_phone,
                      email: data.contact_email || listing.contact_email,
                    });
                    toast.success(data.message || "Owner contact unlocked");
                  } catch (err) {
                    const status = err?.response?.status;
                    if (status === 402) {
                      // Free view already used → kick off the Razorpay paid flow
                      await startPaidUnlock();
                    } else {
                      toast.error(formatErr(err));
                    }
                  } finally {
                    setUnlockLoading(false);
                  }
                }}
                className={`btn-primary w-full justify-center ${unlocked ? "!bg-[#2D6A4F] !shadow-none" : ""}`}
                data-testid="listing-unlock-btn"
              >
                {unlockLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : unlocked ? (
                  <ShieldCheck className="w-4 h-4" />
                ) : (
                  <Lock className="w-4 h-4" />
                )}
                {!isAuthed
                  ? "Sign in to unlock"
                  : unlocked
                  ? "Contact unlocked"
                  : unlockLoading
                  ? "Unlocking…"
                  : `Unlock contact · ${fee}`}
              </a>
              {!isAuthed && (
                <button
                  onClick={() => requireAuth({ redirectTo: `/listing/${id}` })}
                  className="btn-outline w-full justify-center mt-3"
                  data-testid="listing-signin-btn"
                >
                  Create free account
                </button>
              )}

              <div className="mt-5 pt-5 border-t border-[#E4E1D9] text-xs text-[#5C6B62] leading-relaxed">
                Zumroo only reveals owner contacts after a one-time {fee} unlock fee. No
                subscription. No spam.
              </div>
            </motion.div>
          </div>
        </div>
      </main>

      <Footer logoUrl={LOGO_URL} content={content || { contact_phone: "—", contact_email: "—" }} />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#F7F6F2] rounded-xl p-3 border border-[#E4E1D9]">
      <Icon className="w-4 h-4 text-[#1E3F33]" />
      <div className="text-[10px] font-bold uppercase tracking-wider text-[#5C6B62] mt-2">{label}</div>
      <div className="text-sm font-semibold text-[#1A231E]">{value}</div>
    </div>
  );
}
